import { defineStore } from 'pinia';
import { useInputStore } from '@/store/InputStore';
import { useModalStore } from '@/store/ModalStore';
import { usePipeStore } from '@/store/PipeStore';
// eslint-disable-next-line no-unused-vars
import { circles, lines, crosses } from 'svg-patterns';
import stringify  from 'virtual-dom-stringify';
import {usePrintStore} from '@/store/PrintStore';

// https://github.com/derhuerst/svg-patterns?tab=readme-ov-file

export const useSegmentStore = defineStore('segment', {
  state () {
    return {
      pipeSegments: [],
      inlet: {
        position: 0,
        positionY: 0,
        // 1 psi
        pressure: 206842.719
      },
      inletComponent: null,
      selectedPipeNum: null,
      previousPipeNum: 0,
      selectedPipeFromEditor: false,
      selectedSlopeNum: 0,
      previousSlopeNum: 0,
      slopeStart: 0,
      selectedSlopeFromEditor: false,
      allowSplit: false,
      splitX: 0,
      pipeEditorHeight: 0,
      slopeSegments: [],
      maxSlope: 10,

      // color data
      colorOptions: ['#222222', '#666666', '#333333', '#777777', '#444444', '#888888', '#555555', '#999999'],
      colorNum: -1,

      // pattern data
      patterns: [
        {
          type: 'lines',
          style: {
            size: 5, // size of the pattern
            radius: 1,
            complement: true,
            fill: 'blue', // any SVG-compatible color
            strokeWidth: .8,
            stroke: 'black', // any SVG-compatible color
            background: 'red'// any SVG-compatible color
          }
        },
        {
          type: 'circles',
          style: {
            size: 6, // size of the pattern
            radius: 2,
            complement: true,
            fill: '#838383', // any SVG-compatible color
            fillOpacity: .2,
            strokeWidth: 0,
            stroke: 'none', // any SVG-compatible color
            background: 'blue' // any SVG-compatible color
          }
        },
        {
          type: 'circles',
          style: {
            size: 6, // size of the pattern
            radius: 1,
            complement: true,
            fill: '#838383', // any SVG-compatible color
            fillOpacity: .2,
            strokeWidth: 0,
            stroke: 'none', // any SVG-compatible color
            background: 'pink' // any SVG-compatible color
          }
        },
        {
          type: 'circles',
          style: {
            size: 6, // size of the pattern
            radius: 1,
            complement: true,
            fill: '#838383', // any SVG-compatible color
            fillOpacity: .2,
            strokeWidth: 0,
            stroke: 'none', // any SVG-compatible color
            background: 'orange' // any SVG-compatible color
          }
        },
      ],
      patternReferences: [],
      patternDefs: '',
      patternMap: {
        circles, lines, crosses
      },

      // scale data
      gridDivision: 30,
      pipeInlet: null,
      LRBuffer: 20,
      offsets: { left: 0, right: 0 },
      contextVisible: false,
      leftIsUnclamped: false,
      rightIsUnclamped: false,
      trimFunc: null,
      extendFunc: null,
      maxRatio: 1
    };
  },
  getters: {
    getMaxSlope () {
      return this.maxSlope;
    },
    getSlopeYOffset () {
      return this.getPipeEditorHeight * .8;
    },
    getPipeEditorHeight () {
      return this.pipeEditorHeight;
    },
    getSlopeRatio () {
      return this.pipeEditorHeight * .04;
    },
    getPatterns () {
      return this.patterns;
    },
    getContextVisible () {
      return this.contextVisible;
    },
    getWidth () {
      return (document.body.clientWidth * .8) - (this.LRBuffer * 2);
    },
    getGridDivision () {
      return this.gridDivision;
    },
    getAllowSplit () {
      return this.allowSplit;
    },
    getSelectedPipeFromEditor () {
      return this.selectedPipeFromEditor;
    },
    getSelectedSlopeFromEditor () {
      return this.selectedSlopeFromEditor;
    },
    getColorOptions () {
      return this.colorOptions;
    },
    getPreviousPipeNum () {
      return this.previousPipeNum;
    },
    getPreviousSlopeNum () {
      return this.previousSlopeNum;
    },
    getSelectedPipeNum () {
      return this.selectedPipeNum;
    },
    getSelectedSlopeNum () {
      return this.selectedSlopeNum;
    },
    getRightIsUnclamped () {
      return this.rightIsUnclamped;
    },
    getLeftIsUnclamped () {
      return this.leftIsUnclamped;
    },
    getMagScale () {
      let pipeEditorWidth = useInputStore().getPipeEditorWidth;
      if (usePrintStore().getPrintIsOpen) {
        pipeEditorWidth = 1024*.735;
      }
      let pipeLength = this.getAllSegmentLengths('pipe');
      let slopeLength = this.getAllSegmentLengths('slope');
      let length = (pipeLength > slopeLength) ? pipeLength : slopeLength;
      let scale =  pipeEditorWidth / length;
      if (isNaN(scale) || scale === Infinity || scale === -Infinity) {
        scale = 1;
      }
      return scale;
    },
    getLRBuffer () {
      return this.LRBuffer;
    },
    getScaledInletPosition () {
      let position = this.getInletPosition * this.getMagScale;
      if (isNaN(position)) { return 0; }
      return position + this.LRBuffer;
    },
    getInletIndex () {
      return this.pipeSegments.findIndex((pipe) => pipe.isInlet);
    },
    getInletPosition () {
      return this.inlet.position;
    },
    getInletPressure () {
      return this.inlet.pressure;
    },
    getSlopeStart () {
      return this.slopeStart;
    },
    getSprinklerCount () {
      const inputStore = useInputStore();
      return Math.floor((this.getAllSegmentLengths('pipe') - inputStore.getLeftOffset - inputStore.getRightOffset) / inputStore.getSprinklerSpacing);
    },
    getInletPositionY () {
      return this.inlet.positionY;
    },
    getInletComponent () {
      return this.inletComponent;
    },
    getPatternReferences () {
      return this.patternReferences;
    },
    getPatternDefs () {
      return this.patternDefs;
    },
    getSplitPosition () {
      return this.splitX / this.getMagScale;
    },
    nextPipePattern () {
      const usedPatterns = this.pipeSegments.map((segment) => segment.pattern);
      for (var i = 0; i < this.patterns.length; i++) {
        if (!usedPatterns.includes(i)) { return i; }
      }
      return -1;
    },
    nextSlopePattern () {
      const usedPatterns = this.slopeSegments.map((segment) => segment.pattern);
      for (var i = 0; i < this.patterns.length; i++) {
        if (!usedPatterns.includes(i)) { return i; }
      }
      return -1;
    },
    getMaxRatio () {
      return this.maxRatio;
    }
  },
  actions: {
    setMaxRatio (r) {
      this.maxRatio = r;
    },
    getSlopeSegmentStartY (index) {
      return index > 0 ? this.getSlopeSegmentEndY(index - 1) : this.getSlopeStart;
    //  return index > 0 ? this.getSlopeSegmentEndY(index - 1) : this.getSlopeYOffset - this.getSlopeStartY;
    },
    getSlopeSegmentEndY (index) {
      const segment = this.getSegmentOfType('slope', index);
      if(!segment) {
        return 0;
      }

      return this.getSlopeSegmentStartY(index) - (segment.slope * segment.value / 100 * this.getMagScale);
    },
    setPipeEditorHeight (num) {
      this.pipeEditorHeight = num;
    },
    setTrimFunc (func) {
      this.trimFunc = func;
    },
    setExtendFunc (func) {
      this.extendFunc = func;
    },
    createPatterns () {
      this.getPatterns.forEach((pattern) => {
        let createPattern = this.patternMap[pattern.type](pattern.style);
        this.patternReferences.push(createPattern);
        this.patternDefs += stringify(createPattern);
      });
    },
    restoreSegments (storedSegments) {
      this.clearPipes();
      if (storedSegments.pipeSegments.length != 0) {
        this.pipeSegments = storedSegments.pipeSegments;
      }
      this.clearSlopes();
      if (storedSegments.slopeSegments.length != 0) {
        this.slopeSegments = storedSegments.slopeSegments;
      }
      this.setInletPressure(storedSegments.inletPressure);
      this.setInletPosition(storedSegments.inletXPosition);
      this.rightIsUnclamped = storedSegments.rightIsUnclamped;
      this.leftIsUnclamped = storedSegments.leftIsUnclamped;
    },
    setInletPressure (val) {
      this.inlet.pressure = val;
    },
    setInletPosition (val) {
      this.inlet.position = val;
    },
    setInletComponent (val) {
      this.inletComponent = val;
    },
    setInletPositionY (val) {
      this.inlet.positionY = val;
    },
    setSlopeStart (val) {
      this.slopeStart = val;
    },
    toggleIsClamped (side) {
      const newValue = !this.getIsUnclamped(side);
      if (side === 'right') {
        this.rightIsUnclamped = newValue;
      } else {
        this.leftIsUnclamped = newValue;
      }
      useInputStore().sprinklerCalc();
    },
    getIsUnclamped (side) {
      return side === 'right' ? this.rightIsUnclamped : this.leftIsUnclamped;
    },
    setContextVisible (bool) {
      this.contextVisible = bool;
    },
    getOffset (side) {
      const inputStore = useInputStore();
      if (side === 'left') {
        return inputStore.getLeftOffset;
      } else {
        return inputStore.getRightOffset;
      }
    },
    setOffset (side, val) {
      this.offsets[side] = val;
    },
    setOffsetDragged: function (type) {
      const inputStore = useInputStore();
      if (type === null) { type = inputStore.offsetSide; }
      inputStore.offsetSide = type;
      // not for certain if we need this
      const leftOffset = inputStore.getLeftOffset;
      if (this.pipeInlet !== null) {
        let pos = this.getSegmentPosition('pipe', this.pipeInlet.segmentId);
        let prev = this.getPreviousLineSegmentsLength('pipe', pos);
        this.inlet.position = (leftOffset / this.getMagScale) + prev + this.pipeInlet.value;
      }
      // causing jump in inlet when slope is dragged
      if (this.pipeInlet === null) {
        this.inlet.position = leftOffset / this.getMagScale;
      }
    },
    addErrorSlope () {
      let pipeLength = this.getAllSegmentLengths('pipe');
      let slopeLength = this.getAllSegmentLengths('slope');

      // if slope length is less than pipe length, add additional slope
      if (pipeLength === 0 || slopeLength === 0) { return; }
      if (pipeLength < slopeLength) { return; }
      let errorSlope = undefined;
      if (this.numberOfSegmentsOfType('slope') > 1) {
        errorSlope = this.lastSegmentOfType('slope');
      }
      if (errorSlope) {
        let previousSlopeLength = this.getPreviousLineSegmentsLength('slope', this.numberOfSegmentsOfType('slope') - 1);
        errorSlope.value = pipeLength - previousSlopeLength;
        if (errorSlope.value <= 0) {
          this.removeSegment('slope', this.numberOfSegmentsOfType('slope') - 1);
        }
        return;
      }
      if (slopeLength < pipeLength) {
        let psDifference = pipeLength - slopeLength;
        // the parameter true creates an error compensation slope
        let newSegment = this.getDefaultSegment('slope', psDifference, true );
        this.slopeSegments.push(newSegment);
      }
    },
    getSegments (type) {
      if (type === null) {
        return [...this.pipeSegments, ...this.slopeSegments];
      } else {
        return (type === 'pipe') ? this.pipeSegments : this.slopeSegments;
      }
    },
    numberOfSegmentsOfType (type) {
      return this.getSegments(type).length;
    },
    addSegmentForType (type, value) {
      this.getSegments(type).push(this.getDefaultSegment(type, value, false, false));
    },
    addDefaultSegmentForType (type) {
      this.getSegments(type).push(this.getDefaultSegment(type, undefined, false, false));
    },
    changeSelectedPipeSegmentId (id) {
      this.pipeSegments[this.selectedPipeNum].id = id;
    },
    changeSelectedPipeSegmentPattern (patternNum) {
      this.pipeSegments[this.selectedPipeNum].pattern = patternNum;
    },
    changeSelectedSlopeSegmentPattern (patternNum) {
      this.slopeSegments[this.selectedSlopeNum].pattern = patternNum;
    },
    getDefaultSegment (type, value, errorSlope, isInlet) {
      if (value === undefined) {
        value = type === 'pipe' ? 174.8 : 198.4;
      }
      if (errorSlope === undefined) {
        errorSlope = false;
      }
      if (this.colorNum === this.colorOptions.length - 1) {
        this.colorNum = -1;
      }
      this.colorNum++;
      let obj = {
        value: value,
        color: this.colorOptions[this.colorNum],
        pattern: (type === 'pipe') ? this.nextPipePattern : this.nextSlopePattern
      };
      if (errorSlope) {
        // TODO: set to red
        obj.color = this.colorOptions[0];
      }
      // todo: implement 100% accurate rand with while loop
      let rand = Math.round(Math.random() * 999999);
      obj.segmentId = rand;
      if (type === 'pipe') {
        // 62 = 26mm 1.042/42 PE Oval
        obj.id = this.pipeSegments.length == 0 ? 62 : this.pipeSegments[this.pipeSegments.length - 1].id;
      }
      obj.slope = 0;
      if (type === 'pipe' && isInlet) {
        obj.isInlet = true;
      }
      obj.type = type;
      return obj;
    },
    getAllSegmentLengths (type) {
      let x = 0;
      const segments = this.getSegments(type == undefined ? null : type);
      segments.forEach((segment) => { x += segment.value; });
      return x;
    },
    getSegmentOfType (type, index) {
      return this.getSegments(type)[index];
    },
    lastSegmentOfType (type) {
      return this.getSegments(type)[this.numberOfSegmentsOfType(type) - 1];
    },
    getPreviousLineSegmentsLength (type, num) {
      let x = 0;
      const segments = this.getSegments(type);
      for (let a = 0; a < num; a++) { x += segments[a].value; }
      return x;
    },
    getSegmentPosition (type, id) {
      return this.getSegments(type).findIndex((segment) => segment.segmentId === id);
    },
    removeSegment (type, num) {
      this.getSegments(type).splice(num, 1);
    },
    removePreviousSegmentOfType (type) {
      this.removeSegment(type, type === 'pipe' ? this.selectedPipeNum : this.selectedSlopeNum);
    },
    setSelectedPipeNum (num, fromEditor) {
      if (fromEditor === undefined) { fromEditor = true; }
      this.selectedPipeFromEditor = fromEditor;
      this.previousPipeNum = this.selectedPipeNum;
      this.selectedPipeNum = num;
    },
    setSelectedSlopeNum (num, fromEditor) {
      if (fromEditor === undefined) { fromEditor = true; }
      this.selectedSlopeFromEditor = fromEditor;
      this.previousSlopeNum = this.selectedSlopeNum;
      this.selectedSlopeNum = num;
    },
    partial (func /* , 0..n args */) {
      let args = Array.prototype.slice.call(arguments, 1);
      return function () {
        let allArguments = args.concat(Array.prototype.slice.call(arguments));
        return func.apply(this, allArguments);
      };
    },
    checkMatchingInletPipeTypes (segment, newPipe) {
      let p = this.getSegmentPosition('pipe', segment.segmentId);
      let segmentN;
      // bug fix to only having 1 pipe
      if (p === 0) {
        segmentN = this.pipeSegments[p];
      } else if (p == this.pipeSegments.length - 1) {
        segmentN = this.pipeSegments[p];
      } else {
        segmentN = segment.isInlet ? this.pipeSegments[p + 1] : this.pipeSegments[p - 1];
      }
      let segmentNPipe = null;
      if (segmentN !== undefined) {
        segmentNPipe = usePipeStore().getPipeById(segmentN.pipeId);
        // fixes an issue with pipe not being able to be set 
        segment.id = newPipe.id;
      }
      if (!segment.isInlet && segmentN === undefined) {
        segment.id = newPipe.id;
        return;
      }
      if (!segment.isInlet && !segmentN.isInlet) {
        segment.id = newPipe.id;
        return;
      }
      if (newPipe.pipeId !== segmentN.pipeId) {
        const modalStore = useModalStore();
        modalStore.setModalObj({
          promptLabel: 'Pipe Mismatch',
          promptDescription: 'Pipes must be the same type on both sides of the inlet. Which pipe would you like to use?',
          buttonALabel: newPipe.description,
          buttonAFunc: this.partial(this.confirmPipe, segment, segmentN, newPipe),
          buttonBLabel: segmentNPipe.description,
          buttonBFunc: this.partial(this.confirmPipe, segment, segmentN, segmentNPipe),
          buttonCancel: this.handleModalCancel
        });
        modalStore.showModal('prompt');
      }
    },
    handleModalCancel () {
      useModalStore().hideModal();
    },
    confirmPipe (segment, segmentR, selectedPipe) {
      segment.pipeId = selectedPipe.pipeId;
      segmentR.pipeId = selectedPipe.pipeId;
      this.handleModalCancel();
    },
    validateSlopeMinimumLength () {
      let pipeLength = this.getAllSegmentLengths('pipe');
      let slopeLength = this.getAllSegmentLengths('slope');
      if (pipeLength === 0 || slopeLength === 0) { return; }
      if (slopeLength < pipeLength) {
        let dif = Math.abs(slopeLength - pipeLength);
        let lastSlope = this.lastSegmentOfType('slope');
        lastSlope.value += dif;
      }
    },
    setAllowSplit (bool) {
      this.allowSplit = bool;
    },
    setSplitX (x) {
      this.splitX = x;
    },
    splitInlet () {
      const inletPosition = this.inlet.position;
      this.pipeInlet = this.pipeSegments.find((segment) => segment.isInlet);
      const allSegmentLengths = this.getAllSegmentLengths('pipe');
      if (inletPosition == 0) {
        this.rejoinPipes();
        this.refreshInletPipe();
      } else if (inletPosition >= allSegmentLengths) {
        const lastSegment = this.pipeSegments[this.pipeSegments.length - 1];
        if (inletPosition > allSegmentLengths) {
          lastSegment.value += inletPosition - lastSegment.endPosition;
          lastSegment.endPosition = inletPosition;
        }
        lastSegment.isInlet = true;
        this.rejoinPipes();
        this.refreshInletPipe();
      } else {
        this.rejoinPipes();
        this.splitPipe(inletPosition);
      }
    },
    refreshInletPipe () {
      this.pipeSegments.forEach((pipe) => pipe.isInlet = pipe.endPosition == this.inlet.position);
    },
    splitPipe (positionOfSplit) {
      const indexOfSegmentToBeSplit = this.pipeSegments.findIndex((segment) => segment.endPosition > positionOfSplit);
      let segmentToBeSplit = this.pipeSegments[indexOfSegmentToBeSplit];
      const newSegment = this.getDefaultSegment('pipe', segmentToBeSplit.endPosition - positionOfSplit);
      newSegment.id = segmentToBeSplit.id;
      this.pipeSegments.splice(indexOfSegmentToBeSplit + 1, 0, newSegment);
      segmentToBeSplit.value = positionOfSplit - segmentToBeSplit.startPosition;
      segmentToBeSplit.endPosition = positionOfSplit;
      this.refreshInletPipe();
    },
    extendPipes () {
      const slopesLength = this.getAllSegmentLengths('slope');
      const lastPipeSegment = this.pipeSegments[this.pipeSegments.length - 1];
      lastPipeSegment.endPosition = slopesLength;
      lastPipeSegment.value = lastPipeSegment.endPosition - lastPipeSegment.startPosition;
    },
    splitSlope (positionOfSplit) {
      const indexOfSegmentToBeSplit = this.slopeSegments.findIndex((_, index) => this.getPreviousLineSegmentsLength('slope', index + 1) > positionOfSplit);
      let segmentToBeSplit = this.slopeSegments[indexOfSegmentToBeSplit];
      const newSegment = this.getDefaultSegment('slope', this.getPreviousLineSegmentsLength('slope', indexOfSegmentToBeSplit + 1) - positionOfSplit);
      newSegment.id = segmentToBeSplit.id;
      this.slopeSegments.splice(indexOfSegmentToBeSplit + 1, 0, newSegment);
      segmentToBeSplit.value = positionOfSplit - this.getPreviousLineSegmentsLength('slope', indexOfSegmentToBeSplit);
      this.refreshInletPipe();
    },
    trimSlopes () {
      const pipesLength = this.getAllSegmentLengths('pipe');
      var trimmedSlopeSegments = this.slopeSegments.filter((slopeSegment) => slopeSegment.startPosition < pipesLength);
      var lastSlopeSegment = trimmedSlopeSegments[trimmedSlopeSegments.length - 1];
      if (lastSlopeSegment.endPosition > pipesLength) {
        lastSlopeSegment.endPosition = pipesLength;
        lastSlopeSegment.value = lastSlopeSegment.endPosition - lastSlopeSegment.startPosition;
      }
      this.slopeSegments = trimmedSlopeSegments;
    },
    rejoinPipes () {
      let inletPipeIndex = this.getInletIndex;
      let pipeAfterInlet = this.pipeSegments[inletPipeIndex + 1];
      if (pipeAfterInlet != undefined && this.pipeInlet != undefined && this.pipeInlet.id == pipeAfterInlet.id) {
        this.pipeInlet.value += pipeAfterInlet.value;
        this.pipeInlet.endPosition += pipeAfterInlet.value;
        this.pipeSegments.splice(inletPipeIndex + 1, 1);
      }
    },
    getInletCount (side) {
      const inputStore = useInputStore();
      let inletLeftSprinklers = Math.floor((this.getInletPosition - inputStore.getLeftOffset) / inputStore.getSprinklerSpacing);
      return side === 'left' ? inletLeftSprinklers : this.getSprinklerCount - inletLeftSprinklers;
    },
    clearPipesAndSlopes () {
      this.clearPipes();
      this.clearSlopes();
      useInputStore().sprinklerCalc();
    },
    clearPipes () {
      this.pipeSegments = [this.getDefaultSegment('pipe')];
      this.pipeInlet = null;
      this.setInletPosition(0);
    },
    clearSlopes () {
      this.slopeSegments = [this.getDefaultSegment('slope')];
    },

    // utilities
    hexToRgb (hex) {
      hex = hex.replace(/[^0-9A-F]/gi, '');
      let bigint = parseInt(hex, 16);
      let r = (bigint >> 16) & 255;
      let g = (bigint >> 8) & 255;
      let b = bigint & 255;
      return [r, g, b].join();
    },
    getTranslateXY (element) {
      const matrix = new DOMMatrixReadOnly(window.getComputedStyle(element).transform);
      return { translateX: matrix.m41, translateY: matrix.m42 };
    },
    updateSlopes () {
      const totalPipeLength = this.getAllSegmentLengths('pipe');
      const totalSlopeLength = this.getAllSegmentLengths('slope');
      if (totalPipeLength > totalSlopeLength) {
        const lastSlope = this.lastSegmentOfType('slope');
        if (lastSlope.slope == 0) {
          lastSlope.value += totalPipeLength - totalSlopeLength;
          lastSlope.endPosition = lastSlope.startPosition + lastSlope.value;
        } else {
          useModalStore().showModal('slope-extend');
        }
      }
    }
  }
});